'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { create } from 'zustand';

import {
  DONATION_ROUTES,
  ENV,
  MODULE_ROUTES,
  ROUTES,
  SEARCH_PARAMS,
  STORAGE_KEY,
} from '@/constants';
import { useAuthStore } from '@/stores/AuthStore';

import { isCanonicalDetailRedirectSourcePathname } from '../_utils/isCanonicalDetailRedirectSourcePathname';
import { isCanonicalSlugRedirect } from '../_utils/isCanonicalSlugRedirect';
import { isKbHistoryExceptRoute } from '../_utils/kbHistoryExceptRoutes';
import { moveBackInfo } from '../_utils/moveBackInfo';
import { isKbWebview } from '../_utils/nativeCall';
import { setTopMenuV2 } from '../_utils/setTopMenuV2';
import {
  KB_DUMMY_PATHNAME,
  KB_DUMMY_PATHNAME_SPLIT_POINT,
} from './kbHistoryConstants';

export { KB_DUMMY_PATHNAME, KB_DUMMY_PATHNAME_SPLIT_POINT };

interface KbHistoryStore {
  historyList: string[];
  setHistoryList: (setter: (history: string[]) => string[]) => void;
}

const kbHistoryStore = create<KbHistoryStore>((set) => ({
  historyList: [],
  setHistoryList: (setter) =>
    set((state) => ({ historyList: setter(state.historyList) })),
}));

export const KbHistoryStack = () => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const isLogin = useAuthStore((state) => state.isLogin);

  useEffect(() => {
    if (!pathname) return;

    if (!isKbWebview()) {
      const isAllowedPath = [
        '/login',
        '/join',
        '/terms',
        '/easy-certification',
        '/filter',
        '/donation',
      ].some((path) => pathname.startsWith(path));

      if (!isAllowedPath) {
        const originalWello = ENV.NEXT_PUBLIC_DOMAIN;

        location.href = location.href.replace(location.origin, originalWello);
      }
    }
  }, [pathname]);

  useEffect(() => {
    setTopMenuV2('', '', {
      menuBt: 'Y',
      preBt: 'Y',
      close: 'N',
      title: '맞춤형 정책지원금 알림',
      preAction: 'B',
      target: '',
    });
  }, []);

  const isExceptRoute = isKbHistoryExceptRoute(pathname);

  useEffect(() => {
    if (!pathname || isExceptRoute || !isKbWebview() || !isLogin) return;
    if (isCanonicalDetailRedirectSourcePathname(pathname)) return;

    //* /search 카테고리 페이지는 KB back 시 proxy redirect 없이 직접 복원한다.
    //* kbpostparam redirect를 거치면 일부 카테고리 상세 → back에서 에러 페이지가 노출된다.
    const shouldUseKbDummyPathname = pathname !== ROUTES.SEARCH.pathname;
    let kbDummyPathname = pathname;
    const params: { [key: string]: string } = {};
    const url = new URL(pathname, location.origin);

    searchParams?.forEach((value, key) => {
      if (key === SEARCH_PARAMS.POST_REDIRECT) return;

      if (shouldUseKbDummyPathname) {
        const lastChar = kbDummyPathname.slice(-1);

        //! ⚠️ KB 쪽에서 SearchParams가 달라도 Pathname이 같으면 같은 페이지로 인식하기 때문에 Pathname에 파라미터를 붙여주는 로직 미들웨어에서 벗겨내는 로직으로 페이지 이동
        kbDummyPathname =
          kbDummyPathname +
          `${lastChar === '/' ? '' : '/'}${KB_DUMMY_PATHNAME}${KB_DUMMY_PATHNAME_SPLIT_POINT}${key}${KB_DUMMY_PATHNAME_SPLIT_POINT}${value}`;
      } else {
        url.searchParams.set(key, value);
      }

      params[key] = value;
    });

    if (shouldUseKbDummyPathname) {
      url.pathname = kbDummyPathname;
    }

    const { historyList, setHistoryList } = kbHistoryStore.getState();

    const lastHistory = historyList[historyList.length - 1];

    const historyUrl = url.toString();
    const isNewHistory = lastHistory !== historyUrl;

    if (!isNewHistory) return;

    const hasReplaceMarkKb =
      sessionStorage.getItem(STORAGE_KEY.REPLACE_MARK_KB) === 'true';
    if (hasReplaceMarkKb) {
      sessionStorage.removeItem(STORAGE_KEY.REPLACE_MARK_KB);
    }

    switch (pathname) {
      //* 게이트웨이 앱스킴을 통해 앱에 진입 한 경우 강제로 히스토리를 추가해줌. (이전 히스토리가 없기 때문에 뒤로가기 이슈로 인해 추가)
      case ROUTES.GATEWAY.pathname:
        {
          if (historyList.length > 0) return;

          const homeUrl = new URL(
            MODULE_ROUTES.KB_HOME.pathname,
            location.origin,
          ).toString();

          const donationHomeUrl = new URL(
            DONATION_ROUTES.HOME.pathname,
            location.origin,
          ).toString();

          moveBackInfo(homeUrl, {}, 'Y', 'addHistoryStackThisDonationHome');
          setHistoryList(() => [homeUrl, donationHomeUrl]);
        }

        break;
      default:
        //* 상세 페이지의 slug 정규화(`generateMetadata` → `permanentRedirect`)로 인해
        //* 두 번 push되는 것을 방지: canonical id가 동일하면 last entry를 교체한다.
        if (isCanonicalSlugRedirect(lastHistory, pathname)) {
          moveBackInfo(historyUrl, params, 'Y', undefined, true);
          setHistoryList((history) =>
            history.length > 0
              ? [...history.slice(0, -1), historyUrl]
              : [historyUrl],
          );
        } else {
          moveBackInfo(historyUrl, params);
          setHistoryList((history) => [...history, historyUrl]);
        }

        break;
    }
  }, [isExceptRoute, isLogin, pathname, searchParams]);

  return <></>;
};
